import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { pedidosService, Pedido, UpdateEstatusDto, DetallePedido } from '../src/services/pedidosService';
import { empleadosService, Empleado } from '../src/services/empleadosService';
import { detallePedidosService } from '../src/services/detallePedidosService';
import { negociosService, Negocio } from '../src/services/negociosService';
import { useAuth } from '../src/contexts/AuthContext';

interface PedidosAdminProps {
  onClose: () => void;
}

const ESTATUS_OPTIONS = ['Pendiente', 'Preparando', 'EnCamino', 'Entregado', 'Cancelado'];

const ESTATUS_COLORS: Record<string, string> = {
  Pendiente:  'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  Preparando: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  EnCamino:   'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  Entregado:  'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  Cancelado:  'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

const ESTATUS_ICONS: Record<string, string> = {
  Pendiente:  'schedule',
  Preparando: 'soup_kitchen',
  EnCamino:   'two_wheeler',
  Entregado:  'check_circle',
  Cancelado:  'cancel',
};

const isDarkMode = () => {
  const theme = localStorage.getItem('theme');
  if (theme) return theme === 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

const PedidosAdmin: React.FC<PedidosAdminProps> = ({ onClose }) => {
  const { user } = useAuth();
  const isSuperAdmin = user?.nombreRol === 'SuperAdmin';

  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  const [empleados, setEmpleados] = useState<Empleado[]>([]);
  const [negocios, setNegocios] = useState<Negocio[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtroEstatus, setFiltroEstatus] = useState<string>('Todos');
  const [selectedPedido, setSelectedPedido] = useState<Pedido | null>(null);
  const [saving, setSaving] = useState(false);
  const [showDetallesModal, setShowDetallesModal] = useState(false);
  const [detallesLoading, setDetallesLoading] = useState(false);
  const [detallesData, setDetallesData] = useState<DetallePedido[]>([]);
  const [detallesPedidoNum, setDetallesPedidoNum] = useState<string>('');

  const dark = isDarkMode();

  const handleVerDetalles = async (pedido: Pedido) => {
    setDetallesPedidoNum(pedido.numeroPedido);
    setDetallesData([]);
    setShowDetallesModal(true);
    setDetallesLoading(true);
    try {
      const data = await detallePedidosService.getByPedido(pedido.pedidoId);
      setDetallesData(data);
    } catch {
      setDetallesData([]);
    } finally {
      setDetallesLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      Swal.fire({
        icon: 'warning',
        title: 'Acceso Denegado',
        text: 'El usuario no está logueado',
        confirmButtonColor: '#f97316',
        background: dark ? '#1a1614' : '#ffffff',
        color: dark ? '#ffffff' : '#1a1614',
        customClass: {
          popup: 'rounded-2xl'
        }
      }).then(() => onClose());
      return;
    }

    try {
      setLoading(true);
      const [pedidosData, empleadosData, negociosData] = await Promise.all([
        pedidosService.getAll(),
        empleadosService.getAll(),
        negociosService.getAll(),
      ]);
      setNegocios(negociosData);
      
      // Filter pedidos based on user's empresa (via negocios)
      if (isSuperAdmin) {
        setPedidos(pedidosData);
        setEmpleados(empleadosData);
      } else {
        const userEmpresaId = user?.empresaId;
        const negocioIds = negociosData
          .filter(n => n.empresaId === userEmpresaId)
          .map(n => n.negocioId);
        setPedidos(pedidosData.filter(p => negocioIds.includes(p.negocioId)));
        setEmpleados(empleadosData.filter(e => e.empresaId === userEmpresaId));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleCambiarEstatus = async (pedido: Pedido, nuevoEstatus: string) => {
    const { value: descripcion } = await Swal.fire({
      title: `Cambiar a "${nuevoEstatus}"`,
      input: 'text',
      inputLabel: 'Descripción (opcional)',
      inputPlaceholder: 'Ej: Pedido listo para enviar...',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#f97316',
      background: dark ? '#1a1614' : '#ffffff',
      color: dark ? '#ffffff' : '#1a1614',
      customClass: {
        popup: 'rounded-2xl'
      }
    });

    if (descripcion === undefined) return;

    try {
      setSaving(true);
      const dto: UpdateEstatusDto = {
        estatus: nuevoEstatus,
        descripcion: descripcion || null,
      };
      const updated = await pedidosService.updateEstatus(pedido.pedidoId, dto);
      setPedidos(prev => prev.map(p => p.pedidoId === updated.pedidoId ? updated : p));
      if (selectedPedido?.pedidoId === updated.pedidoId) setSelectedPedido(updated);
      Swal.fire({
        icon: 'success',
        title: 'Estatus actualizado',
        timer: 1500,
        showConfirmButton: false,
        background: dark ? '#1a1614' : '#ffffff',
        color: dark ? '#ffffff' : '#1a1614',
        customClass: {
          popup: 'rounded-2xl'
        }
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err instanceof Error ? err.message : 'No se pudo actualizar el estatus',
        confirmButtonColor: '#f97316',
        background: dark ? '#1a1614' : '#ffffff',
        color: dark ? '#ffffff' : '#1a1614',
        customClass: {
          popup: 'rounded-2xl'
        }
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAsignarRepartidor = async (pedido: Pedido) => {
    if (empleados.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Sin repartidores',
        text: 'No hay repartidores registrados.',
        confirmButtonColor: '#f97316',
        background: dark ? '#1a1614' : '#ffffff',
        color: dark ? '#ffffff' : '#1a1614',
        customClass: {
          popup: 'rounded-2xl'
        }
      });
      return;
    }

    const options: Record<string, string> = {};
    empleados.forEach(e => {
      options[e.empleadoId] = e.disponible ? e.nombreEmpleado : `${e.nombreEmpleado} (no disponible)`;
    });

    const { value: empleadoId } = await Swal.fire({
      title: 'Asignar Repartidor',
      input: 'select',
      inputOptions: options,
      inputPlaceholder: 'Selecciona un repartidor',
      showCancelButton: true,
      confirmButtonText: 'Asignar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#3b82f6',
      cancelButtonColor: '#64748b',
      background: dark ? '#1a1614' : '#ffffff',
      color: dark ? '#ffffff' : '#1a1614',
      customClass: {
        popup: 'rounded-2xl'
      }
    });

    if (!empleadoId) return;

    try {
      setSaving(true);
      const updated = await pedidosService.update(pedido.pedidoId, {
        empleadoId: Number(empleadoId),
        direccionEntrega: pedido.direccionEntrega,
        total: pedido.total,
        estatus: pedido.estatus,
        latitud: pedido.latitud,
        longitud: pedido.longitud,
        llegadaEstimada: pedido.llegadaEstimada,
      });
      setPedidos(prev => prev.map(p => p.pedidoId === updated.pedidoId ? updated : p));
      if (selectedPedido?.pedidoId === updated.pedidoId) setSelectedPedido(updated);
      Swal.fire({
        icon: 'success',
        title: 'Repartidor asignado',
        timer: 1500,
        timerProgressBar: true,
        showConfirmButton: false,
        background: dark ? '#1a1614' : '#ffffff',
        color: dark ? '#ffffff' : '#1a1614',
        customClass: {
          popup: 'rounded-2xl'
        }
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err instanceof Error ? err.message : 'No se pudo asignar el repartidor',
        confirmButtonColor: '#ef4444',
        background: dark ? '#1a1614' : '#ffffff',
        color: dark ? '#ffffff' : '#1a1614',
        customClass: {
          popup: 'rounded-2xl'
        }
      });
    } finally {
      setSaving(false);
    }
  };

  const pedidosFiltrados = filtroEstatus === 'Todos'
    ? pedidos
    : pedidos.filter(p => p.estatus === filtroEstatus);

  const contarPorEstatus = (est: string) => pedidos.filter(p => p.estatus === est).length;

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });

  const siguienteEstatus = (actual: string): string | null => {
    const flujo = ['Pendiente', 'Preparando', 'EnCamino', 'Entregado'];
    const idx = flujo.indexOf(actual);
    return idx >= 0 && idx < flujo.length - 1 ? flujo[idx + 1] : null;
  };

  return (
    <div className="pt-4 fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-4 overflow-y-auto">
      <div className="bg-white dark:bg-[#1a1614] w-full sm:max-w-6xl min-h-screen sm:min-h-0 sm:rounded-2xl shadow-2xl flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-7 border-b border-gray-100 dark:border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-primary">local_shipping</span>
            </div>
            <div>
              <h2 className="text-2xl text-secondary dark:text-white">Pedidos</h2>
              <p className="text-xs text-gray-400">{pedidos.length} pedidos en total</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadData} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10 transition-colors" title="Recargar">
              <span className="material-symbols-outlined text-gray-400">refresh</span>
            </button>
            <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10 transition-colors">
              <span className="material-symbols-outlined text-gray-400">close</span>
            </button>
          </div>
        </div>

        {/* Status filter tabs */}
        <div className="flex gap-2 px-6 py-3 overflow-x-auto border-b border-gray-100 dark:border-white/10">
          {['Todos', ...ESTATUS_OPTIONS].map(est => (
            <button
              key={est}
              onClick={() => setFiltroEstatus(est)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-colors ${
                filtroEstatus === est
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/10'
              }`}
            >
              {est !== 'Todos' && <span className="material-symbols-outlined text-sm">{ESTATUS_ICONS[est]}</span>}
              {est}
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-black ${filtroEstatus === est ? 'bg-white/20' : 'bg-gray-200 dark:bg-white/10'}`}>
                {est === 'Todos' ? pedidos.length : contarPorEstatus(est)}
              </span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex flex-1 overflow-hidden">

          {/* Orders list */}
          <div className={`flex flex-col overflow-y-auto ${selectedPedido ? 'hidden sm:flex sm:w-1/2' : 'flex-1'} border-r border-gray-100 dark:border-white/10`}>
            {loading ? (
              <div className="flex items-center justify-center flex-1 py-16">
                <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center flex-1 py-16 text-center px-6">
                <span className="material-symbols-outlined text-4xl text-red-400 mb-2">error</span>
                <p className="text-red-500 font-bold">{error}</p>
                <button onClick={loadData} className="mt-3 px-4 py-2 bg-primary text-white rounded-xl text-sm font-bold">Reintentar</button>
              </div>
            ) : pedidosFiltrados.length === 0 ? (
              <div className="flex flex-col items-center justify-center flex-1 py-16 text-center px-6">
                <span className="material-symbols-outlined text-4xl text-gray-300 mb-2">inbox</span>
                <p className="text-gray-400 font-bold">No hay pedidos con este estatus</p>
              </div>
            ) : (
              pedidosFiltrados.map(pedido => (
                <button
                  key={pedido.pedidoId}
                  onClick={() => setSelectedPedido(pedido)}
                  className={`text-left px-5 py-4 border-b border-gray-100 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors ${
                    selectedPedido?.pedidoId === pedido.pedidoId ? 'bg-primary/5 border-l-2 border-l-primary' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-black text-gray-900 dark:text-white truncate">{pedido.numeroPedido}</p>
                      <p className="text-xs text-gray-500 truncate">{pedido.nombreCliente} • {pedido.telefonoCliente}</p>
                      <p className="text-xs text-gray-400 truncate mt-0.5">{pedido.direccionEntrega}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${ESTATUS_COLORS[pedido.estatus]}`}>
                        {pedido.estatus}
                      </span>
                      <span className="text-xs font-bold text-primary">${pedido.total.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[10px] text-gray-400">{formatDate(pedido.fechaCreacion)}</span>
                    <span className="text-[10px] text-gray-400">
                      {pedido.nombreEmpleado
                        ? <><span className="material-symbols-outlined text-xs">two_wheeler</span> {pedido.nombreEmpleado}</>
                        : <span className="text-yellow-500">Sin repartidor</span>}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Order detail */}
          {selectedPedido ? (
            <div className="flex-1 flex flex-col overflow-y-auto">
              {/* Detail header */}
              <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100 dark:border-white/10">
                <button onClick={() => setSelectedPedido(null)} className="sm:hidden p-1.5 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10">
                  <span className="material-symbols-outlined text-gray-400">arrow_back</span>
                </button>
                <div className="flex-1">
                  <p className="text-sm font-black text-gray-900 dark:text-white">{selectedPedido.numeroPedido}</p>
                  <p className="text-xs text-gray-400">{formatDate(selectedPedido.fechaCreacion)}</p>
                </div>
                <button
                  onClick={() => handleVerDetalles(selectedPedido)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 dark:bg-orange-500/10 text-primary rounded-xl text-xs font-bold hover:bg-orange-100 dark:hover:bg-orange-500/20 transition-colors"
                  title="Ver productos del pedido"
                >
                  <span className="material-symbols-outlined text-sm">receipt_long</span>
                  Productos
                </button>
                <span className={`text-xs font-black px-3 py-1 rounded-full ${ESTATUS_COLORS[selectedPedido.estatus]}`}>
                  {selectedPedido.estatus}
                </span>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

                {/* Client info */}
                <div className="bg-gray-50 dark:bg-white/5 rounded-xl p-4">
                  <p className="text-xs font-black text-gray-400 uppercase mb-2">Cliente</p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">{selectedPedido.nombreCliente}</p>
                  <p className="text-xs text-gray-500">{selectedPedido.telefonoCliente}</p>
                  <p className="text-xs text-gray-400 mt-1">{selectedPedido.direccionEntrega}</p>
                </div>

                {/* Negocio */}
                <div className="bg-gray-50 dark:bg-white/5 rounded-xl p-4">
                  <p className="text-xs font-black text-gray-400 uppercase mb-1">Negocio</p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">{selectedPedido.nombreNegocio}</p>
                </div>

                {/* Desglose de totales */}
                <div className="bg-gray-50 dark:bg-white/5 rounded-xl p-4 space-y-2">
                  <p className="text-xs font-black text-gray-400 uppercase mb-3">Resumen de Cobro</p>
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>Subtotal</span>
                    <span className="font-bold">${selectedPedido.subtotal?.toFixed(2) ?? '—'}</span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>Tarifa de servicio</span>
                    <span className="font-bold">${selectedPedido.tarifaServicio?.toFixed(2) ?? '—'}</span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>Costo de envío</span>
                    <span className="font-bold">${selectedPedido.importeEnvio?.toFixed(2) ?? '—'}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-white/10">
                    <span className="text-sm font-black text-gray-700 dark:text-white">Total</span>
                    <span className="text-base font-black text-primary">${selectedPedido.total.toFixed(2)}</span>
                  </div>
                </div>

                {/* Repartidor */}
                <div className="bg-gray-50 dark:bg-white/5 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-black text-gray-400 uppercase mb-1">Repartidor</p>
                      {selectedPedido.nombreEmpleado ? (
                        <p className="text-sm font-bold text-gray-900 dark:text-white ">{selectedPedido.nombreEmpleado}</p>
                      ) : (
                        <p className="text-sm text-yellow-500 font-bold">Sin asignar</p>
                      )}
                    </div>
                    <button
                      onClick={() => handleAsignarRepartidor(selectedPedido)}
                      disabled={saving}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white rounded-xl text-xs font-bold hover:bg-primary/90 transition-colors disabled:opacity-50 "
                    >
                      <span className="material-symbols-outlined text-sm">person_add</span>
                      {selectedPedido.nombreEmpleado ? 'Cambiar' : 'Asignar'}
                    </button>
                  </div>
                </div>

                {/* Status actions */}
                <div>
                  <p className="text-xs font-black text-gray-400 uppercase mb-3">Cambiar Estatus</p>
                  <div className="grid grid-cols-2 gap-2">
                    {ESTATUS_OPTIONS.filter(e => e !== selectedPedido.estatus).map(est => (
                      <button
                        key={est}
                        onClick={() => handleCambiarEstatus(selectedPedido, est)}
                        disabled={saving}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 transition-colors text-left disabled:opacity-50 ${
                          est === siguienteEstatus(selectedPedido.estatus)
                            ? 'border-primary bg-primary/5 text-primary'
                            : 'border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20 text-gray-600 dark:text-gray-300'
                        }`}
                      >
                        <span className="material-symbols-outlined text-base">{ESTATUS_ICONS[est]}</span>
                        <span className="text-xs font-bold">{est}</span>
                        {est === siguienteEstatus(selectedPedido.estatus) && (
                          <span className="ml-auto text-[9px] font-black bg-primary text-white px-1.5 py-0.5 rounded-full">SIGUIENTE</span>
                        )}
                      </button>
                    ))}
                    {selectedPedido.estatus !== 'Cancelado' && (
                      <button
                        onClick={() => handleCambiarEstatus(selectedPedido, 'Cancelado')}
                        disabled={saving}
                        className="flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/10 text-red-500 hover:border-red-300 transition-colors col-span-2 disabled:opacity-50"
                      >
                        <span className="material-symbols-outlined text-base">cancel</span>
                        <span className="text-xs font-bold">Cancelar Pedido</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Seguimientos */}
                <div>
                  <p className="text-xs font-black text-gray-400 uppercase mb-3">Historial de Seguimiento</p>
                  {selectedPedido.seguimientos.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-4">Sin seguimientos registrados</p>
                  ) : (
                    <div className="space-y-2">
                      {[...selectedPedido.seguimientos].reverse().map(seg => (
                        <div key={seg.seguimientoId} className="flex gap-3 items-start">
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${ESTATUS_COLORS[seg.estatus] || 'bg-gray-100'}`}>
                            <span className="material-symbols-outlined text-sm">{ESTATUS_ICONS[seg.estatus] || 'circle'}</span>
                          </div>
                          <div className="flex-1">
                            <p className="text-xs font-bold text-gray-900 dark:text-white">{seg.estatus}</p>
                            {seg.descripcion && <p className="text-xs text-gray-500">{seg.descripcion}</p>}
                            <p className="text-[10px] text-gray-400">{formatDate(seg.fechaCreacion)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="hidden sm:flex flex-1 items-center justify-center text-center px-6">
              <div>
                <span className="material-symbols-outlined text-5xl text-gray-200 dark:text-white/10">local_shipping</span>
                <p className="text-gray-400 font-bold mt-2">Selecciona un pedido</p>
                <p className="text-xs text-gray-300 dark:text-gray-500">para ver detalles y gestionar</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detalles Modal */}
      {showDetallesModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-[#1a1614] w-full max-w-md rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">

            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                  <span className="material-symbols-outlined text-primary text-lg">receipt_long</span>
                </div>
                <div>
                  <p className="text-sm font-black text-gray-900 dark:text-white">Productos del Pedido</p>
                  <p className="text-xs text-gray-400 font-mono">{detallesPedidoNum}</p>
                </div>
              </div>
              <button
                onClick={() => setShowDetallesModal(false)}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              >
                <span className="material-symbols-outlined text-gray-400">close</span>
              </button>
            </div>

            {/* Modal body */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {detallesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin w-7 h-7 border-2 border-primary border-t-transparent rounded-full"></div>
                </div>
              ) : detallesData.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <span className="material-symbols-outlined text-4xl text-gray-200 dark:text-white/10 mb-2">fastfood</span>
                  <p className="text-gray-400 font-bold text-sm">Sin productos registrados</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {detallesData.map((det: DetallePedido) => (
                    <div key={det.detalleId} className="flex items-center gap-3 bg-gray-50 dark:bg-white/5 rounded-xl p-3">
                      {det.imagenUrl ? (
                        <img src={det.imagenUrl} alt={det.nombreProducto} className="w-14 h-14 rounded-xl object-cover shrink-0" />
                      ) : (
                        <div className="w-14 h-14 rounded-xl bg-gray-200 dark:bg-white/10 flex items-center justify-center shrink-0">
                          <span className="material-symbols-outlined text-gray-400 text-2xl">fastfood</span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-black text-gray-900 dark:text-white leading-tight">{det.nombreProducto}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{det.cantidad} unidad(es) × ${det.precioUnitario.toFixed(2)}</p>
                      </div>
                      <span className="text-sm font-black text-primary shrink-0">${det.subtotal.toFixed(2)}</span>
                    </div>
                  ))}

                  {/* Total row */}
                  <div className="flex justify-between items-center pt-3 mt-1 border-t border-gray-100 dark:border-white/10">
                    <span className="text-xs font-black text-gray-400 uppercase tracking-wide">Total productos</span>
                    <span className="text-base font-black text-primary">
                      ${detallesData.reduce((acc, d) => acc + d.subtotal, 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div className="px-5 py-3 border-t border-gray-100 dark:border-white/10">
              <button
                onClick={() => setShowDetallesModal(false)}
                className="w-full py-2.5 rounded-xl bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-white text-sm font-bold hover:bg-gray-200 dark:hover:bg-white/20 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PedidosAdmin;

